// Translation.
//
// One flat table per language, keyed the same everywhere, with English as the
// source of truth: t() falls back to it for any key a translation has not filled
// in, so a half-finished language shows English in the gaps instead of blank
// space or a raw key name. Adding a language is one file plus one line below,
// which is the whole reason the tables are plain data and not functions.
//
// Nothing here imports the game. The game imports this.

import EN from './locales/en.js';
import ES from './locales/es.js';
import IT from './locales/it.js';
import FR from './locales/fr.js';
import DE from './locales/de.js';
import RU from './locales/ru.js';

/**
 * Native names on purpose: a language list in the wrong language helps nobody.
 *
 * The flag is a two-letter key and the colours are drawn in the stylesheet, because a flag is
 * the one thing a flag emoji cannot be on Windows: there are no flag glyphs in the system font,
 * so the character arrives as the two letters of its code inside a box — in the one place where
 * a flag is the whole point of the thing.
 */
const TABLES = {
  en: { label: 'ENGLISH', flag: 'en', table: EN },
  es: { label: 'ESPAÑOL', flag: 'es', table: ES },
  it: { label: 'ITALIANO', flag: 'it', table: IT },
  fr: { label: 'FRANÇAIS', flag: 'fr', table: FR },
  de: { label: 'DEUTSCH', flag: 'de', table: DE },
  ru: { label: 'РУССКИЙ', flag: 'ru', table: RU },
};

/** The languages on offer, in the order the picker shows them. */
export const LANGS = Object.keys(TABLES).map(id => ({ id, label: TABLES[id].label, flag: TABLES[id].flag }));

/** English is the default, and the fallback for every language including itself. */
export const DEFAULT_LANG = 'en';

/** Where the player's choice is kept. Deliberately its own key: it is a setting,
 *  not progress, and wiping a save should not reset the language. */
const LANG_KEY = 'qbasicgorillas/lang/v1';

let active = DEFAULT_LANG;
let table = EN;

/**
 * The string for a key, with `{name}` placeholders filled from `vars`. An unknown
 * key returns itself, which makes a missing translation obvious on screen instead
 * of silently printing "undefined".
 */
export function t(key, vars) {
  const raw = table[key] ?? EN[key] ?? key;
  if (!vars) return raw;

  return raw.replace(/\{(\w+)\}/g, (whole, name) => (vars[name] !== undefined ? vars[name] : whole));
}

/** The language in use. */
export function lang() {
  return active;
}

/**
 * Switches language, remembers it, and rewrites the markup. Returns the id that
 * ended up in use, so a caller can tell whether it was accepted.
 */
export function setLang(id, { persist = true } = {}) {
  active = TABLES[id] ? id : DEFAULT_LANG;
  table = TABLES[active].table;

  // The document language matters beyond us: it is what a screen reader picks a
  // voice from, and what the browser offers to translate from.
  document.documentElement.lang = active;

  if (persist) {
    try {
      localStorage.setItem(LANG_KEY, active);
    } catch {
      // A browser refusing to store a preference is not a reason to stop playing.
    }
  }

  applyDom();
  return active;
}

/** The saved choice, or the default. Never throws: a bad value just means English. */
export function savedLang() {
  try {
    return localStorage.getItem(LANG_KEY) || DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

/**
 * Fills in everything in the markup tagged for translation. Dynamic text is not
 * this function's business — every panel already redraws itself from t() when it
 * opens, so this only has to handle the static chrome.
 */
export function applyDom(root = document) {
  root.querySelectorAll('[data-i18n]').forEach(el => {
    el.textContent = t(el.dataset.i18n);
  });

  root.querySelectorAll('[data-i18n-placeholder]').forEach(el => {
    el.placeholder = t(el.dataset.i18nPlaceholder);
  });

  root.querySelectorAll('[data-i18n-title]').forEach(el => {
    el.title = t(el.dataset.i18nTitle);
  });

  root.querySelectorAll('[data-i18n-label]').forEach(el => {
    el.setAttribute('aria-label', t(el.dataset.i18nLabel));
  });
}
